import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { api } from "../../api/apiService";
import { normalizeAnalyticProfilesResponse } from "../../utils/analyticProfilesResponse";
import { cardSx, errorText, fieldSx, menuProps } from "./fields";
import { ModuleGrid } from "./ModuleGrid";

type Skill = {
  id: string;
  name: string;
  description: string;
  builtin: boolean;
  modules?: string[];
  visibility?: string;
  profile_ids?: string[];
  owner?: string;
};

type ProfileOption = { id: string; name: string };

const visibilityOptions = [
  { value: "private", label: "Private" },
  { value: "profiles", label: "Analytic profiles" },
  { value: "company", label: "Anyone in the company" },
];

function visibilityLabel(value: string | undefined): string {
  return visibilityOptions.find((item) => item.value === value)?.label || "Anyone in the company";
}

function asSkills(data: unknown): Skill[] {
  if (!Array.isArray(data)) return [];
  return data.filter((row): row is Skill => Boolean(row) && typeof row === "object" && typeof (row as Skill).id === "string");
}

export function SkillsList() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [modules, setModules] = useState<string[]>([]);
  const [visibility, setVisibility] = useState("private");
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileOption[]>([]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editVisibility, setEditVisibility] = useState("private");
  const [editProfiles, setEditProfiles] = useState<ProfileOption[]>([]);

  const load = useCallback(async () => {
    const [skillData, profileData] = await Promise.all([
      api.get("/api/agent-studio/skills"),
      api.get("/api/analytic-profiles/all").catch(() => []),
    ]);
    setSkills(asSkills(skillData));
    const options = normalizeAnalyticProfilesResponse(profileData)
      .map((row) => ({
        id: String(row.id || row._id || ""),
        name: String(row.name || "Untitled profile"),
      }))
      .filter((row) => row.id);
    setProfiles(options);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setFormError(errorText(err, "Could not load skills."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const profileNames = (ids: string[] | undefined) => {
    if (!ids?.length) return "";
    return ids
      .map((id) => profiles.find((profile) => profile.id === id)?.name || id)
      .join(", ");
  };

  const handleFile = (next: File | null) => {
    setFile(next);
    if (next && !name.trim()) {
      setName(next.name.replace(/\.[^.]+$/, ""));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setFormError("Choose a skill file to upload.");
      return;
    }
    if (visibility === "profiles" && selectedProfiles.length === 0) {
      setFormError("Choose at least one analytic profile.");
      return;
    }
    const body = new FormData();
    body.append("file", file);
    body.append("name", name.trim());
    body.append("visibility", visibility);
    if (modules.length === 0) body.append("modules", "");
    modules.forEach((id) => body.append("modules", id));
    selectedProfiles.forEach((profile) => body.append("profile_ids", profile.id));
    setSaving(true);
    setFormError(null);
    try {
      await api.post("/api/agent-studio/skills", body);
      setFile(null);
      setName("");
      setModules([]);
      setVisibility("private");
      setSelectedProfiles([]);
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not upload this skill."));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (skill: Skill) => {
    setEditingId(skill.id);
    setEditName(skill.name);
    setEditVisibility(skill.visibility || "company");
    setEditProfiles(profiles.filter((profile) => (skill.profile_ids || []).includes(profile.id)));
    setFormError(null);
  };

  const saveEdit = async (skill: Skill) => {
    if (editVisibility === "profiles" && editProfiles.length === 0) {
      setFormError("Choose at least one analytic profile.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.put(`/api/agent-studio/skills/${skill.id}`, {
        name: editName.trim(),
        description: skill.description,
        visibility: editVisibility,
        profile_ids: editProfiles.map((profile) => profile.id),
        modules: skill.modules || [],
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not update this skill."));
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async (skill: Skill, next: string[]) => {
    setSkills((prev) => prev.map((item) => (item.id === skill.id ? { ...item, modules: next } : item)));
    setFormError(null);
    try {
      await api.put(`/api/agent-studio/skills/${skill.id}`, { modules: next });
    } catch (err) {
      setFormError(errorText(err, "Could not update where this skill is available."));
      try {
        await load();
      } catch {
        // The previous error already explains the failure.
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this skill?")) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.delete(`/api/agent-studio/skills/${id}`);
      if (editingId === id) setEditingId(null);
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not delete this skill."));
    } finally {
      setSaving(false);
    }
  };

  const profilePicker = (
    value: ProfileOption[],
    onChange: (next: ProfileOption[]) => void
  ) => (
    <Autocomplete
      multiple
      options={profiles}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      disabled={saving}
      renderInput={(params) => <TextField {...params} label="Analytic profiles" size="small" sx={fieldSx} />}
      componentsProps={{
        paper: { sx: { bgcolor: "#0a0a0a", color: "#fafafa", border: "1px solid #27272a" } },
      }}
    />
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ color: "#fafafa", fontWeight: 600 }}>
          Skills
        </Typography>
        <Typography variant="body2" sx={{ color: "#a1a1aa", mt: 0.5 }}>
          Upload a skill file, then check the modules where it should be available. Keep it private, share it with analytic profiles, or make it available to anyone in the company.
        </Typography>
      </Box>

      {formError && <Alert severity="error">{formError}</Alert>}

      <Stack spacing={2} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ color: "#e4e4e7" }}>
          Upload a skill
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} flexWrap="wrap">
          <Button component="label" variant="outlined" disabled={saving} sx={{ color: "#e4e4e7", borderColor: "#52525b", mt: { md: "4px" } }}>
            {file ? file.name : "Choose file"}
            <input
              hidden
              type="file"
              accept=".md,.txt,.json,text/plain,text/markdown,application/json"
              onChange={(event) => handleFile(event.target.files?.[0] || null)}
            />
          </Button>
          <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 160 } }}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
          </Box>
          <Box sx={{ flex: 1.2, minWidth: { xs: "100%", md: 220 } }}>
            <TextField
              select
              label="Who can use it"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              size="small"
              disabled={saving}
              sx={fieldSx}
              SelectProps={{ MenuProps: menuProps }}
            >
              {visibilityOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Tooltip title="Upload skill">
            <span>
              <IconButton aria-label="Upload skill" disabled={saving || !file || !name.trim()} onClick={() => void handleUpload()} sx={{ color: "#818cf8" }}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {visibility === "profiles" && profilePicker(selectedProfiles, setSelectedProfiles)}
        <ModuleGrid value={modules} onChange={setModules} disabled={saving} />
      </Stack>

      {loading && (
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          Loading skills…
        </Typography>
      )}

      {skills.map((skill) => (
        <Stack key={skill.id} spacing={1.5} sx={cardSx}>
          {editingId === skill.id ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
                <TextField
                  select
                  label="Who can use it"
                  value={editVisibility}
                  onChange={(e) => setEditVisibility(e.target.value)}
                  size="small"
                  disabled={saving}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: menuProps }}
                >
                  {visibilityOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {editVisibility === "profiles" && profilePicker(editProfiles, setEditProfiles)}
              <Stack direction="row">
                <Tooltip title="Save">
                  <span>
                    <IconButton aria-label="Save skill" disabled={saving || !editName.trim()} onClick={() => void saveEdit(skill)} sx={{ color: "#818cf8" }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton aria-label="Cancel edit" onClick={() => setEditingId(null)} sx={{ color: "#a1a1aa" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ color: "#fafafa", fontWeight: 600 }}>{skill.name}</Typography>
                  {skill.builtin ? (
                    <Chip size="small" label="Library" sx={{ color: "#a5b4fc", borderColor: "#4338ca" }} variant="outlined" />
                  ) : (
                    <Chip size="small" label={visibilityLabel(skill.visibility)} sx={{ color: "#e4e4e7", borderColor: "#3f3f46" }} variant="outlined" />
                  )}
                </Stack>
                {skill.description && (
                  <Typography variant="body2" sx={{ color: "#a1a1aa", mt: 0.5 }}>
                    {skill.description}
                  </Typography>
                )}
                {!skill.builtin && skill.visibility === "profiles" && (
                  <Typography variant="caption" sx={{ color: "#71717a" }}>
                    {profileNames(skill.profile_ids) || "No profiles selected"}
                  </Typography>
                )}
                {!skill.builtin && skill.visibility === "private" && skill.owner && (
                  <Typography variant="caption" sx={{ color: "#71717a", display: "block" }}>
                    Only {skill.owner}
                  </Typography>
                )}
              </Box>
              {!skill.builtin && (
                <Stack direction="row">
                  <Tooltip title="Edit">
                    <IconButton aria-label={`Edit ${skill.name}`} onClick={() => startEdit(skill)} sx={{ color: "#a1a1aa" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton aria-label={`Delete ${skill.name}`} disabled={saving} onClick={() => void handleDelete(skill.id)} sx={{ color: "#a1a1aa" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          )}
          <ModuleGrid value={skill.modules || []} disabled={saving} onChange={(next) => void saveModules(skill, next)} />
        </Stack>
      ))}
    </Stack>
  );
}
